/**
 * Shared config types. Rates are data, not code: every value the engine prices
 * with is wrapped in a {@link RateEntry} so the UI can show its provenance and
 * the rep can override it (overrides are labelled on the printed summary).
 */

/** A dated, sourced configuration value. */
export interface RateEntry<T = number> {
  /** The value. Money is in USD, energy rates in $/kWh, fixed fees in $/month. */
  readonly value: T;
  /** Human-readable unit, e.g. "$/kWh", "$/month", "fraction". */
  readonly unit: string;
  /** ISO date (YYYY-MM-DD) the value took effect / was set. */
  readonly effectiveDate: string;
  /** Where the value comes from (URL, document, or "Business input, <date>"). */
  readonly source: string;
  /** True while the value is an unconfirmed assumption. The UI shows a ⚠ badge. */
  readonly placeholder?: boolean;
  /** Optional caveat for the rep drawer. */
  readonly note?: string;
  /**
   * Other published values the rep may switch to in the drawer (e.g. a value
   * from a different primary source or a pre-rider rate).
   */
  readonly alternates?: readonly RateAlternate<T>[];
}

/** An alternate published value for a {@link RateEntry}. */
export interface RateAlternate<T = number> {
  readonly value: T;
  readonly effectiveDate: string;
  readonly source: string;
  /** Short label for the drawer toggle, e.g. "Pre-Docket 59249". */
  readonly label: string;
}

/** Texas Transmission & Distribution Utilities in scope. */
export type TduId = "oncor" | "centerpoint" | "tnmp";

/** Texas weather regions (DESIGN §3.3). */
export type RegionId = "dfw" | "central" | "gulf" | "west";

/** Contract term lengths offered by Octopus. */
export type TermMonths = 12 | 18 | 24;

/** All supported terms, in display order. */
export const TERM_OPTIONS: readonly TermMonths[] = [12, 18, 24] as const;

/**
 * Which charges solar buyback credits may offset.
 * - `energy_only`: REP energy charges only (placeholder default).
 * - `entire_bill`: energy + TDU + base fee.
 */
export type BuybackApplyMode = "energy_only" | "entire_bill";

/** Home heating archetype (carried over from ES). */
export type LoadArchetype = "electric_heat" | "gas_heat";
